import React, { useState } from "react";
import { Box, Flex, Grid, Image, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickUnsyncConfirmModal from "@/features/pathpick/components/request/PathPickUnsyncConfirmModal";
import PathPickStartGuideKreamUploadGuideModal from "@/features/pathpick/features/start-guide/components/after-order/PathPickStartGuideKreamUploadGuideModal";
import PathPickStartGuideSmartStoreUploadGuideModal from "@/features/pathpick/features/start-guide/components/after-order/PathPickStartGuideSmartStoreUploadGuideModal";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import { getProductImageUrl } from "@/features/pathpick/utils";
import { DEMO_PRODUCTS } from "@/features/pathpick/features/start-guide/utils/demoProducts";
import { StartGuideSalesMode } from "@/features/pathpick/features/start-guide/types";
import {
  DemoProductState,
  StartGuideDemoState,
} from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";

interface Props {
  demo: StartGuideDemoState;
  // 판매 방식마다 이 화면이 하는 일이 다르다 — 연동 관리 / 엑셀 다운로드 / KREAM 다운로드.
  salesMode: StartGuideSalesMode;
  // `신청 후 이용` 탭 안에서 이 화면이 몇 번째인지 — 단계 표시와 이동 라벨이 여기서 갈린다.
  stepIndex: number;
  stepTotal: number;
  onManageProduct: (id: number, operation: "unlink" | "delete") => void;
  onPrev: () => void;
  onNext: () => void;
}

// 전달서 §12 가 지정한 4열. 실제 등록 신청 내역은 신청 단위 표라 열 구성이 다르다.
// 관리 열은 가장 긴 `KREAM 파일 다운로드` 버튼 기준이다 — 앞 세 열에서 폭을 걷어 여기에 얹었다.
const TEMPLATE_COLUMNS = "minmax(220px, 2fr) 100px minmax(140px, 1fr) 220px";

// 상태별 배지와 관리 셀 표시. 연동 상태일 때만 관리 버튼이 나온다.
const STATUS_META: Record<
  DemoProductState,
  { labelKey: string; noteKey?: string; color: "green" | "gray" }
> = {
  // `saved` 는 아직 등록 목 처리를 거치지 않은 상태다 — API 흐름은 배지 대신 `-` 를 그리고
  // 파일 흐름은 아래에서 `status_file_ready` 로 덮으므로 이 항목은 Record 를 채우기 위해서만 남는다.
  saved: { labelKey: "status_synced", color: "green" },
  linked: { labelKey: "status_synced", color: "green" },
  "file-ready": { labelKey: "status_file_ready", color: "green" },
  unlinked: {
    labelKey: "status_disconnected",
    noteKey: "note_disconnected",
    color: "gray",
  },
  deleted: {
    labelKey: "status_deleted",
    noteKey: "note_deleted",
    color: "gray",
  },
};

const REQUEST_HISTORY_KEY = "path_pick_guide:request_history.";

/**
 * 등록 신청 내역 안내 — 판매 방식에 따라 세 가지 얼굴을 갖는다.
 *
 * 스마트스토어 API: 주문 앞에 놓이고, 연동 해제와 상품 삭제의 차이를 보여주는 것이 유일한 목적이라
 * 안내 단계는 1개뿐이고 오류 원인·재처리 단계는 두지 않는다(전달서 §12 "제거 대상").
 * 확인 모달은 운영에서 쓰는 `PathPickUnsyncConfirmModal` 을 그대로 연다.
 *
 * 스마트스토어 엑셀: 생성된 일괄 등록용 엑셀 파일을 받는 자리를 보여준다. 연동이 없어 관리할 것도
 * 없으므로 관리 열이 다운로드 버튼이 되고, 다운로드는 토스트로 체험을 마친다.
 * 파일을 받은 뒤 할 일(판매자센터 업로드와 등록 예시)은 그 아래 `등록 예시 보기` 링크가 여는 모달이 맡는다.
 *
 * KREAM 전용: KREAM 주문 상세에서 파일 생성을 신청한 뒤에 놓인다. 신청한 입찰용 파일을 받는 자리를
 * 보여주는 것이 목적이라 관리 열이 다운로드 버튼이 되고, 그 아래 `등록 예시 보기` 링크가
 * 파일 두 종류를 각각 어디에 올리는지 안내하는 KREAM 전용 모달을 연다(입찰가 입력 안내도 그 안에 들어 있다).
 */
function PathPickStartGuideRequestHistoryTour({
  demo,
  salesMode,
  stepIndex,
  stepTotal,
  onManageProduct,
  onPrev,
  onNext,
}: Props) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });

  const isKreamOnly = salesMode === "kream";
  // 엑셀·KREAM 은 연동이 아니라 만들어진 파일을 받는 흐름이다 — 표의 뒷부분이 통째로 달라진다.
  const hasFileDownload = salesMode !== "api";

  // 문구는 기본값(API) 위에 판매 방식 접미사를 얹는 방식으로 갈린다.
  const key = (base: string) =>
    `${REQUEST_HISTORY_KEY}${base}${
      salesMode === "api" ? "" : `_${salesMode}`
    }`;

  // 탭의 양 끝에서만 탭 밖으로 나간다 — 그때만 `단계` 라벨을 쓴다.
  const isFirstPage = stepIndex === 0;
  const isLastPage = stepIndex === stepTotal - 1;
  // 관리 버튼을 누른 상품 (null이면 모달 닫힘)
  const [targetId, setTargetId] = useState<number | null>(null);
  // `등록 예시 보기` 로 여는 판매 채널별 업로드 안내
  const [isUploadGuideOpen, setIsUploadGuideOpen] = useState(false);
  // 다운로드를 눌러 본 행 — 그 행에만 `등록 예시 보기` 링크가 따라 붙는다.
  const [downloadedIds, setDownloadedIds] = useState<number[]>([]);

  // 담은 상품 두 개를 항상 그대로 보여준다 — 담기·선택이 두 상품을 모두 요구하므로
  // 등록 상태로 목록을 거를 이유가 없고, 거르면 한 상품을 해제하는 순간 나머지가 표에서 사라진다.
  const items = DEMO_PRODUCTS;
  const stateOf = (id: number): DemoProductState =>
    demo.productStateById[id] ?? "linked";
  // 앞 단계(연동 신청)를 실행하지 않은 채 탭으로 건너뛴 행 —
  // prepareForStep 이 채워 두는 기본값이 `saved` 다.
  const isPendingPrevStep = (id: number) =>
    !hasFileDownload && stateOf(id) === "saved";
  // 파일 흐름은 파일이 만들어진 뒤에 닿는 화면이므로 전부 신청·완료로 본다.
  const requestedCount = hasFileDownload
    ? items.length
    : items.filter((item) => !isPendingPrevStep(item.id)).length;
  const completedCount = hasFileDownload
    ? items.length
    : items.filter((item) =>
        ["linked", "file-ready"].includes(stateOf(item.id))
      ).length;

  // 다운로드 자리만 보여주는 화면이다 — 실제 파일 대신 완료 토스트로 그 단계를 체험한다.
  // 받은 뒤 어디에 올리는지는 이어서 나타나는 `등록 예시 보기` 링크가 맡는다.
  const runDownload = (id: number) => {
    info(t(`${REQUEST_HISTORY_KEY}download_toast`));
    setDownloadedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const apply = (operation: "unlink" | "delete") => {
    if (targetId !== null) {
      onManageProduct(targetId, operation);
    }
    setTargetId(null);
  };

  return (
    <>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
              <Text
                textStyle={"body-lg-bold"}
                fontSize={"22px"}
                color={"#1D1D1B"}
              >
                {t(`${REQUEST_HISTORY_KEY}title`)}
              </Text>
              {/* 판매 방식에 따라 이 탭의 화면 수가 달라진다 — 한 장뿐이면 표시하지 않는다. */}
              {stepTotal > 1 && (
                <Text
                  textStyle={"caption-bold"}
                  color={"#1272EF"}
                  role={"status"}
                  aria-label={t("path_pick_guide:nav.step_aria_label")}
                >
                  {t("path_pick_guide:nav.step_label", {
                    current: stepIndex + 1,
                    total: stepTotal,
                  })}
                </Text>
              )}
            </Flex>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t(key("description"))}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        <Flex flexDirection={"column"} gap={"16px"}>
          {/* 이동 버튼은 이 단계 패널의 우측 하단에 붙는다 — 다른 탭과 같다. */}
          <PathPickStartGuideStepPanel
            title={t(key("step_title"))}
            description={t(key("step_description"))}
            actions={
              <Flex gap={"8px"}>
                <AppButton variant={"normal"} onClick={onPrev}>
                  {t(
                    isFirstPage
                      ? "path_pick_guide:nav.prev_step"
                      : "path_pick_guide:nav.prev"
                  )}
                </AppButton>
                <AppButton variant={"primary"} onClick={onNext}>
                  {t(
                    isLastPage
                      ? "path_pick_guide:nav.next_step"
                      : "path_pick_guide:nav.next"
                  )}
                </AppButton>
              </Flex>
            }
          />

          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"12px"}
            flexWrap={"wrap"}
          >
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${REQUEST_HISTORY_KEY}date_line`)}
            </Text>
            {/* 요약은 신청/오류/완료까지만 — 사용량 숫자는 넣지 않는다(전달서 §12). */}
            <Text textStyle={"body-sm-bold"} color={"#444440"} role={"status"}>
              {t(`${REQUEST_HISTORY_KEY}summary`, {
                requested: requestedCount,
                failed: 0,
                completed: completedCount,
              })}
            </Text>
          </Flex>

          {/* 긴 표는 가로 스크롤을 허용한다(전달서 §21). */}
          <Box overflowX={"auto"}>
            <Box
              minWidth={"fit-content"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              overflow={"hidden"}
              role={"table"}
              aria-label={t(`${REQUEST_HISTORY_KEY}table_aria_label`)}
            >
              <Grid
                templateColumns={TEMPLATE_COLUMNS}
                backgroundColor={"#F7F9FA"}
                borderBottom={"0.5px solid #D4D4D1"}
              >
                {(["product", "channel", "status", "manage"] as const).map(
                  (columnKey) => (
                    <Text
                      key={columnKey}
                      padding={"10px 16px"}
                      textStyle={"caption-bold"}
                      color={"#6F6F6C"}
                    >
                      {t(`${REQUEST_HISTORY_KEY}column.${columnKey}`)}
                    </Text>
                  )
                )}
              </Grid>

              {items.map((item, index) => {
                const state = stateOf(item.id);
                const meta = STATUS_META[state];
                // 파일 흐름에는 연동 상태 자체가 없다 — 상태는 항상 파일 생성 완료다.
                const isManageable = !hasFileDownload && state === "linked";
                // 앞 단계를 마치지 않은 행은 상태·관리 열을 안내로 대신한다.
                const isPending = isPendingPrevStep(item.id);
                return (
                  <Grid
                    key={item.id}
                    templateColumns={TEMPLATE_COLUMNS}
                    alignItems={"center"}
                    borderBottom={
                      index < items.length - 1
                        ? "0.5px solid #EDEEF0"
                        : undefined
                    }
                  >
                    <Flex
                      gap={"10px"}
                      alignItems={"center"}
                      padding={"12px 16px"}
                      minWidth={0}
                    >
                      <Image
                        src={getProductImageUrl(item)}
                        fallbackSrc={"/icons-v1_1/null.png"}
                        alt={""}
                        width={"40px"}
                        height={"40px"}
                        objectFit={"contain"}
                        flexShrink={0}
                        backgroundColor={"#FFFFFF"}
                        border={"0.5px solid #EDEEF0"}
                        borderRadius={"4px"}
                      />
                      <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
                        <Text
                          textStyle={"body-sm-bold"}
                          color={"#22221F"}
                          noOfLines={1}
                        >
                          {`${item.brandName} · ${item.name}`}
                        </Text>
                        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                          {`SKU ${item.sku}`}
                        </Text>
                      </Flex>
                    </Flex>
                    <Text
                      padding={"12px 16px"}
                      textStyle={"body-sm-medium"}
                      color={"#444440"}
                    >
                      {t(
                        item.productType === "LINE_SHEET_PRODUCT"
                          ? `${REQUEST_HISTORY_KEY}channel_hot_deal`
                          : `${REQUEST_HISTORY_KEY}channel_normal`
                      )}
                    </Text>
                    <Box padding={"12px 16px"}>
                      {isPending ? (
                        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                          {"-"}
                        </Text>
                      ) : (
                        <PathPickBadge
                          label={t(
                            hasFileDownload
                              ? `${REQUEST_HISTORY_KEY}status_file_ready`
                              : `${REQUEST_HISTORY_KEY}${meta.labelKey}`
                          )}
                          colorVariant={hasFileDownload ? "green" : meta.color}
                          size={"medium"}
                        />
                      )}
                    </Box>
                    <Box padding={"12px 16px"}>
                      {/* 파일 흐름은 관리 대신 다운로드다 — 만들어진 파일을 받는 자리를 보여준다.
                          API 는 연동 중인 상품에만 관리 버튼을 두고,
                          끝난 행은 결과 문구만 남긴다. */}
                      {isPending ? (
                        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                          {t(`${REQUEST_HISTORY_KEY}manage_todo`)}
                        </Text>
                      ) : hasFileDownload ? (
                        <Flex
                          flexDirection={"column"}
                          alignItems={"center"}
                          gap={"6px"}
                        >
                          <AppButton
                            variant={"white"}
                            onClick={() => runDownload(item.id)}
                          >
                            {t(
                              isKreamOnly
                                ? `${REQUEST_HISTORY_KEY}download_cta`
                                : `${REQUEST_HISTORY_KEY}download_cta_file`
                            )}
                          </AppButton>
                          {/* 파일을 받아 본 뒤에야 "이제 어디에 올리나"가 궁금해진다 —
                              그때 링크로 나타난다. 테두리·배경 없이 글자색만 바뀌는 링크형 버튼. */}
                          {downloadedIds.includes(item.id) && (
                            <Text
                              as={"button"}
                              type={"button"}
                              textStyle={"caption-bold"}
                              color={"#1272EF"}
                              _hover={{ color: "#005AAD" }}
                              onClick={() => setIsUploadGuideOpen(true)}
                            >
                              {t(key("example_cta"))}
                            </Text>
                          )}
                        </Flex>
                      ) : isManageable ? (
                        <AppButton
                          variant={"white"}
                          onClick={() => setTargetId(item.id)}
                        >
                          {t(`${REQUEST_HISTORY_KEY}manage_cta`)}
                        </AppButton>
                      ) : (
                        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                          {meta.noteKey
                            ? t(`${REQUEST_HISTORY_KEY}${meta.noteKey}`)
                            : "-"}
                        </Text>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Box>
          </Box>

          <PathPickInlineNotice tone={"gray"} leadingIcon={"info-circle"}>
            {t(key("explainer"))}
          </PathPickInlineNotice>
        </Flex>
      </PathPickStartGuideCard>

      {/* 올리는 자리가 채널마다 달라 모달도 갈린다 — 엑셀은 판매자센터 한 곳, KREAM 은 두 단계다. */}
      {hasFileDownload &&
        (isKreamOnly ? (
          <PathPickStartGuideKreamUploadGuideModal
            isOpen={isUploadGuideOpen}
            onClose={() => setIsUploadGuideOpen(false)}
          />
        ) : (
          <PathPickStartGuideSmartStoreUploadGuideModal
            isOpen={isUploadGuideOpen}
            onClose={() => setIsUploadGuideOpen(false)}
          />
        ))}
      {salesMode === "api" && (
        <PathPickUnsyncConfirmModal
          isOpen={targetId !== null}
          onClose={() => setTargetId(null)}
          count={1}
          isLoading={false}
          onUnsyncOnly={() => apply("unlink")}
          onDelete={() => apply("delete")}
        />
      )}
    </>
  );
}

export default PathPickStartGuideRequestHistoryTour;
